import React, { useMemo, useState, useEffect } from 'react';
import './RankingColumnSettingPanel.css';

function RankingColumnSettingPanel({
  groups = [],
  onClose,
  onToggleGroup,
  onToggleItem,
  onReorderGroup,
  screenSettingEnabled = false,
  screenSettingItems = [],
  selectedScreenSettingId = '',
  screenSettingBusy = false,
  hasUnsavedChanges = false,
  onSelectScreenSetting,
  onSaveScreenSetting,
  onDeleteScreenSettingItem,
  onReorderScreenSetting,
  onScreenSettingOrderChange,
}) {
  const GROUP_DRAG_TYPE = 'application/x-ranking-group';
  const SCREEN_SETTING_DRAG_TYPE = 'application/x-screen-setting';
  const [dialogState, setDialogState] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [dragFromIndex, setDragFromIndex] = useState(null);
  const [localScreenSettingItems, setLocalScreenSettingItems] = useState(screenSettingItems);

  // Sync local state with props when they change
  useEffect(() => {
    setLocalScreenSettingItems(screenSettingItems);
  }, [screenSettingItems]);

  const handleDragStart = (event, groupId) => {
    event.dataTransfer.setData(GROUP_DRAG_TYPE, groupId);
    event.dataTransfer.effectAllowed = 'move';
  };

  const handleDrop = (event, targetGroupId) => {
    event.preventDefault();
    const sourceGroupId = event.dataTransfer.getData(GROUP_DRAG_TYPE);
    if (!sourceGroupId || sourceGroupId === targetGroupId) return;
    if (typeof onReorderGroup === 'function') onReorderGroup(sourceGroupId, targetGroupId);
  };

  const handleOverwriteSave = async () => {
    // 현재 선택된 설정을 덮어쓰기
    const selectedSetting = screenSettingItems.find(s => String(s?.settingId) === String(selectedScreenSettingId));
    if (!selectedSetting) return;

    // 먼저 화면설정 저장
    await onSaveScreenSetting?.({
      mode: 'overwrite',
      settingId: Number(selectedScreenSettingId),
      settingName: String(selectedSetting.settingName || '').trim(),
    });
  };

  const handleSaveOrderOnly = async () => {
    // 순서만 저장
    const settingIds = localScreenSettingItems.map(s => s.settingId);
    await onReorderScreenSetting?.(settingIds);
  };

  const openNewSaveDialog = () => {
    setDialogState({
      type: 'new',
      settingName: '',
    });
  };

  const openDeleteDialog = (setting) => {
    setDialogState({
      type: 'delete',
      settingId: String(setting?.settingId || ''),
      settingName: String(setting?.settingName || ''),
    });
  };

  const handleClose = () => {
    if (hasUnsavedChanges) {
      setDialogState({
        type: 'confirm-close',
      });
      return;
    }
    onClose?.();
  };

  const closeDialog = () => setDialogState(null);

  const submitDialog = async () => {
    if (!dialogState || screenSettingBusy) return;

    try {
      if (dialogState.type === 'confirm-close') {
        closeDialog();
        onClose?.();
        return;
      }
      if (dialogState.type === 'new') {
        await onSaveScreenSetting?.({
          mode: 'new',
          settingId: null,
          settingName: String(dialogState.settingName || '').trim(),
        });
        closeDialog();
        return;
      }
      if (dialogState.type === 'delete') {
        const settingIdToDelete = Number(dialogState.settingId);
        closeDialog(); // 모달 먼저 닫기
        await onDeleteScreenSettingItem?.(settingIdToDelete);
      }
    } catch (error) {
      console.error('Dialog submit error:', error);
    }
  };

  return (
    <div className="complex-column-setting-modal-backdrop" onClick={handleClose}>
      <div
        className={`complex-column-setting-modal complex-column-setting-panel-grouped ${screenSettingEnabled ? 'with-screen-settings' : ''}`.trim()}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="complex-column-setting-head">
          <strong>화면설정</strong>
          <div className="complex-column-setting-head-actions">
            {screenSettingEnabled && selectedScreenSettingId && String(selectedScreenSettingId).trim() !== '' && (
              <>
                <button type="button" onClick={openNewSaveDialog} disabled={screenSettingBusy}>신규등록</button>
                <button type="button" onClick={handleOverwriteSave} disabled={screenSettingBusy}>저장</button>
              </>
            )}
            {screenSettingEnabled && hasUnsavedChanges && (!selectedScreenSettingId || String(selectedScreenSettingId).trim() === '') && (
              <button type="button" onClick={handleSaveOrderOnly} disabled={screenSettingBusy}>순서 저장</button>
            )}
            <button type="button" onClick={handleClose}>닫기</button>
          </div>
        </div>

        <div className="complex-column-setting-layout">
          <section className="complex-column-edit-pane">
            {screenSettingEnabled && (
              <section className="complex-screen-setting-pane">
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', marginBottom: '8px' }}>
                  <div className="complex-screen-setting-pane-title">저장된 화면설정</div>
                  <div style={{ fontSize: '12px', color: '#dc2626', fontWeight: '700' }}>
                    드래그로 화면설정 순서를 변경할 수 있습니다
                  </div>
                </div>
                <div className="complex-screen-setting-pane-list">
                  {localScreenSettingItems.length === 0 && (
                    <div className="complex-screen-setting-empty">저장된 설정이 없습니다.</div>
                  )}
                  {localScreenSettingItems.map((setting, index) => {
                    const settingId = String(setting?.settingId || '');
                    const active = settingId === String(selectedScreenSettingId || '');
                    const isDragOver = dragOverIndex === index;

                    // Determine drop position border
                    let dropBorderStyle = {};
                    if (isDragOver && dragFromIndex !== null) {
                      if (dragFromIndex < index) {
                        // Dragging down: show bottom border
                        dropBorderStyle = { borderBottom: '2px solid #fb923c' };
                      } else {
                        // Dragging up: show top border
                        dropBorderStyle = { borderTop: '2px solid #fb923c' };
                      }
                    }

                    return (
                      <div
                        key={settingId}
                        className={`complex-screen-setting-item ${active ? 'active' : ''} ${screenSettingBusy ? 'loading' : ''} ${isDragOver ? 'drag-over' : ''}`.trim()}
                        style={{
                          backgroundColor: active ? '#fff7ed' : '#fff',
                          borderLeft: active ? '1px solid #fb923c' : '1px solid #dbe3ee',
                          borderRight: active ? '1px solid #fb923c' : '1px solid #dbe3ee',
                          ...dropBorderStyle,
                          transition: 'all 0.2s ease'
                        }}
                        onDragOver={(e) => {
                          e.preventDefault();
                          e.dataTransfer.dropEffect = 'move';
                          setDragOverIndex(index);
                        }}
                        onDragLeave={(e) => {
                          // Only clear if we're actually leaving the item, not entering a child
                          if (e.currentTarget === e.target || !e.currentTarget.contains(e.relatedTarget)) {
                            setDragOverIndex(null);
                          }
                        }}
                        onDrop={(e) => {
                          e.preventDefault();
                          setDragOverIndex(null);
                          setDragFromIndex(null);
                          const fromIndex = parseInt(e.dataTransfer.getData(SCREEN_SETTING_DRAG_TYPE), 10);
                          if (!isNaN(fromIndex) && fromIndex !== index) {
                            const reorderedItems = [...localScreenSettingItems];
                            const [movedItem] = reorderedItems.splice(fromIndex, 1);
                            reorderedItems.splice(index, 0, movedItem);
                            setLocalScreenSettingItems(reorderedItems);
                            // 부모에게 순서가 변경되었음을 알림
                            onScreenSettingOrderChange?.(reorderedItems);
                          }
                        }}
                      >
                        <span
                          className="complex-screen-setting-item-drag-handle"
                          draggable={!screenSettingBusy}
                          onDragStart={(e) => {
                            e.dataTransfer.effectAllowed = 'move';
                            e.dataTransfer.setData(SCREEN_SETTING_DRAG_TYPE, String(index));
                            e.currentTarget.style.cursor = 'grabbing';
                            setDragFromIndex(index);
                          }}
                          onDragEnd={(e) => {
                            e.currentTarget.style.cursor = 'grab';
                            setDragOverIndex(null);
                            setDragFromIndex(null);
                          }}
                          style={{
                            cursor: screenSettingBusy ? 'default' : 'grab',
                            color: '#64748b',
                            fontSize: '14px',
                            userSelect: 'none',
                            padding: '0 6px',
                            display: 'flex',
                            alignItems: 'center'
                          }}
                        >
                          ☰
                        </span>
                        <button
                          type="button"
                          className="complex-screen-setting-item-main"
                          onClick={() => onSelectScreenSetting?.(settingId)}
                          disabled={screenSettingBusy}
                        >
                          {screenSettingBusy && active ? (
                            <div className="complex-screen-setting-item-spinner">
                              <div className="mini-spinner"></div>
                            </div>
                          ) : null}
                          <span className="name">{setting?.settingName || '-'}</span>
                          <span className="meta">{setting?.updatedAt || '-'}</span>
                        </button>
                        <button
                          type="button"
                          className="complex-screen-setting-item-delete"
                          onClick={(e) => {
                            e.stopPropagation();
                            openDeleteDialog(setting);
                          }}
                          disabled={screenSettingBusy}
                          title="삭제"
                          aria-label="삭제"
                        >
                          삭제
                        </button>
                      </div>
                    );
                  })}
                </div>
              </section>
            )}

            <div className="complex-column-quick-order-wrap">
              <div className="complex-column-quick-order-title">
                빠른 순서 편집
              </div>
              <div style={{ fontSize: '12px', color: '#dc2626', fontWeight: '700', marginBottom: '8px' }}>
                드래그로 정렬 순서를 변경할 수 있습니다
              </div>
              {screenSettingBusy ? (
                <div className="complex-column-quick-order-loading">
                  <div className="spinner"></div>
                  <p>불러오는 중...</p>
                </div>
              ) : screenSettingEnabled && (!selectedScreenSettingId || String(selectedScreenSettingId).trim() === '') ? (
                <div className="complex-column-quick-order-empty">
                  <p>저장된 화면설정을 선택하거나 새로 만들어주세요</p>
                  <button type="button" className="btn-primary" onClick={openNewSaveDialog} disabled={screenSettingBusy}>
                    신규 등록
                  </button>
                </div>
              ) : (
                <div className="complex-column-quick-order-list">
                  {groups.map((group) => (
                  <div
                    key={`quick-${group.id}`}
                    className={`complex-column-quick-order-row ${group.enabled ? '' : 'group-disabled'}`.trim()}
                    draggable
                    onDragStart={(event) => handleDragStart(event, group.id)}
                    onDragOver={(event) => event.preventDefault()}
                    onDrop={(event) => handleDrop(event, group.id)}
                    title="드래그로 그룹 순서 변경"
                  >
                    <div className="complex-column-quick-order-row-head">
                      <span className="complex-column-quick-order-grip">{String.fromCharCode(9776)}</span>
                      <label className={`complex-column-group-check ${group.locked ? 'locked' : ''}`.trim()}>
                        <input
                          type="checkbox"
                          checked={group.enabled}
                          disabled={group.locked}
                          onChange={() => {
                            if (!group.locked) onToggleGroup(group.id);
                          }}
                        />
                        <span>{group.name}</span>
                      </label>
                    </div>
                    <div className="complex-column-quick-order-row-items">
                      {(Array.isArray(group.items) ? group.items : []).map((item) => {
                        const itemId = String(item?.id || '');
                        const itemLabel = String(item?.label || item || '');
                        const itemEnabled = item?.enabled !== false;
                        const itemLocked = item?.locked === true;
                        return (
                          <div key={itemId || itemLabel} className="complex-column-quick-order-item">
                            <label className={`complex-column-item-check ${itemLocked ? 'locked' : ''}`.trim()}>
                              <input
                                type="checkbox"
                                checked={itemEnabled}
                                onChange={() => {
                                  if (!itemLocked) onToggleItem?.(group.id, itemId);
                                }}
                                disabled={itemLocked}
                              />
                              <span>{itemLabel}</span>
                            </label>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  ))}
                </div>
              )}
            </div>
            {(!screenSettingEnabled || (selectedScreenSettingId && String(selectedScreenSettingId).trim() !== '')) && (
              <div className="complex-column-setting-footnote">체크박스로 표시 여부를 바로 켜고 끌 수 있습니다.</div>
            )}

            {dialogState && (
              <div className="complex-screen-setting-dialog-backdrop" onClick={closeDialog}>
                <div className="complex-screen-setting-dialog" onClick={(event) => event.stopPropagation()}>
                  <div className="complex-screen-setting-dialog-head">
                    <strong>
                      {dialogState.type === 'new' && '신규 화면설정 등록'}
                      {dialogState.type === 'delete' && '화면설정 삭제'}
                      {dialogState.type === 'confirm-close' && '저장하지 않은 변경사항'}
                    </strong>
                  </div>

                  {dialogState.type === 'new' && (
                    <div className="complex-screen-setting-dialog-body">
                      <div className="complex-screen-setting-dialog-field">
                        <label>설정 이름</label>
                        <input
                          type="text"
                          value={dialogState.settingName || ''}
                          onChange={(event) => setDialogState((prev) => ({ ...prev, settingName: event.target.value }))}
                          placeholder="설정 이름을 입력해 주세요"
                        />
                      </div>
                    </div>
                  )}

                  {dialogState.type === 'delete' && (
                    <div className="complex-screen-setting-dialog-body">
                      <p className="complex-screen-setting-dialog-desc">"{dialogState.settingName || '-'}" 설정을 삭제할까요?</p>
                    </div>
                  )}

                  {dialogState.type === 'confirm-close' && (
                    <div className="complex-screen-setting-dialog-body">
                      <p className="complex-screen-setting-dialog-desc">저장하지 않은 변경사항이 있습니다. 저장하지 않고 종료하시겠습니까?</p>
                    </div>
                  )}

                  <div className="complex-screen-setting-dialog-actions">
                    <button type="button" onClick={closeDialog} disabled={screenSettingBusy}>취소</button>
                    <button type="button" onClick={submitDialog} disabled={screenSettingBusy}>
                      {dialogState.type === 'delete' ? '삭제' : dialogState.type === 'confirm-close' ? '종료' : '저장'}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}

export default RankingColumnSettingPanel;
